import { getStyleDeclaration } from './get-style-declaration.js';
import { styleOverride } from './style-override.js';
import { px2em, px2rem } from '../../px2em/@impl/px2em.js';
import {
  activeState,
  getOutlineDeclaration,
  hoverState,
  outliner,
  parentActiveState,
  parentHoverState,
  relativeBorderRadius,
  widgetSelector,
} from './helpers';
import invariant from 'fbjs/lib/invariant';

/**
 * Формирует css rules для виджета кнопка
 * 1. Координаты / width / height
 * 2. Border radius
 *
 * Далее настройки, которые могут быть отличны для разных состояний кнопки:
 *
 * TV: default / focus
 * WEB: default / hover / click
 * MOB: default / tap
 * 3. Text: размер шрифта / color / opacity
 * 4. Background: color / opacity
 * 5. Stroke: ширина линии / color / opacity
 * 6. Scale
 * @example
 *
 * @param {ButtonWidgetDescription} widgetDescription
 * @return {*[]}
 */
export function buttonWidget(widgetDescription) {
  const { styledStates, placements, borderRadius, autoClick, version, width } =
    widgetDescription;
  const selector = widgetSelector(widgetDescription);
  const rules = [
    ...placements.map(getStyleDeclaration.positions(selector)),
    ...placements.map(getStyleDeclaration.size(selector)),
  ];
  rules.push(...styleOverride(widgetDescription));

  if (widgetDescription._isFixedWidth) {
    rules.push([`${selector} > *`, [['width', '100%']]]);
    rules.push([
      selector,
      [
        ['overflow-wrap', 'break-word'],
        ['white-space', 'pre-wrap'],
        // Steel couldn't understand, how it should be?
        // [ 'width', CSS.px(widgetDescription.width) ]
      ],
    ]);
  }

  if (Number.isFinite(borderRadius)) {
    const hasTextChild =
      'text' in widgetDescription && widgetDescription.text !== null;
    const appliedBorderRadius = hasTextChild
      ? px2rem(borderRadius)
      : relativeBorderRadius(borderRadius, widgetDescription);

    try {
      invariant(
        false === appliedBorderRadius.includes('NaN'),
        'NaN in borderRadius',
      );
      rules.push([`${selector}`, [['border-radius', appliedBorderRadius]]]);
    } catch (error) {
      console.error(error);
    }
  }

  if (styledStates) {
    const { default: base, hover, click: active } = styledStates;

    /*
     due to Chrome 47 does not support css variables at all need to explicitly
     define background styles for each state.
     Weired selector as result af generating bg id in rewire-backgrounds.js
     and applying it in bg-layer.jsx component
    */
    const bgSelector = `#bg-layer-fill_${widgetDescription.id}`;

    rules.push([bgSelector, [['background', base.fillColor]]]);

    rules.push([
      parentHoverState(selector, bgSelector),
      [['background-color', hover.fillColor]],
    ]);

    rules.push([
      parentActiveState(selector, bgSelector),
      [['background-color', active.fillColor]],
    ]);

    if (base.scale && base.textColor && base.fontSize) {
      rules.push([
        `${selector}`,
        [
          ['transform', `scale(${base.scale})`],
          ['color', base.textColor],
          ['font-size', px2em(base.fontSize)],
        ],
      ]);
      rules.push([outliner(selector), getOutlineDeclaration(base)]);

      rules.push([
        hoverState(selector),
        [
          ['transform', `scale(${hover.scale})`],
          ['color', hover.textColor],
          ['font-size', px2em(hover.fontSize)],
        ],
      ]);

      rules.push([
        hoverState(selector).map(outliner),
        getOutlineDeclaration(hover),
      ]);

      rules.push([
        activeState(selector),
        [
          ['transform', `scale(${active.scale})`],
          ['color', active.textColor],
          ['font-size', px2em(active.fontSize)],
        ],
      ]);

      rules.push([
        activeState(selector).map(outliner),
        getOutlineDeclaration(active),
      ]);
    }
  }

  if (autoClick) {
    const {
      default: defaultStyle,
      hover: hoverStyle,
      click: activeStyle,
    } = autoClick.appearance;
    const strokeWidth = autoClick.appearance.strokeWidth;
    const gap = defaultStyle.gap ?? 0;
    let size = `calc(${CSS.percent(100)} + ${strokeWidth}px)`;
    let position = `-${CSS.percent(strokeWidth / 2).toString()}`;

    if (version === 2) {
      // TODO: too tricky, find better approach
      const relativeStrokeWidth = Math.round((strokeWidth / width) * 100);
      const relativeGap = Math.round((gap / width) * 100);
      const border = relativeStrokeWidth + relativeGap;

      const positionGap = gap > 0 ? gap : gap / strokeWidth / 2;
      position = `calc(-${CSS.percent(border)} - ${positionGap}px)`;

      const sizeGap = gap > 0 ? gap : Math.abs(gap) / strokeWidth;
      size = `calc(${CSS.percent(gap > -2 ? 100 + border * 2 : 100 + border)} + ${sizeGap}px)`;
    }

    const indicatorSelector = `${selector} [data-role="countdown-indicator"]`;

    // stale rules
    rules.push([
      indicatorSelector,
      [
        ['top', position],
        ['left', position],
        ['width', size],
        ['height', size],
        ['overflow', 'visible'],
      ],
    ]);

    // states depended
    rules.push([`${indicatorSelector} path`, [['stroke', defaultStyle.color]]]);

    rules.push([
      parentHoverState(selector, '[data-role="countdown-indicator"] path'),
      [['stroke', hoverStyle?.color]],
    ]);

    rules.push([
      parentActiveState(selector, '[data-role="countdown-indicator"] path'),
      [['stroke', activeStyle?.color]],
    ]);
  }

  return rules;
}

/**
 * Represents a button widget with various properties.
 * @typedef {Object} ButtonWidgetDescription
 * @property {string} id - The unique identifier of the button widget.
 * @property {string} type - The type of the widget, which is "button" in this case.
 * @property {number} x - The horizontal position of the widget.
 * @property {number} y - The vertical position of the widget.
 * @property {number} width - The width of the widget.
 * @property {number} height - The height of the widget.
 * @property {number} borderRadius - The border radius of the widget in px.
 * @property {Array} backgrounds - An array of background objects associated with the widget.
 * @property {string} linkType - The <del>type of link<del><ins>link to target</ins> associated with the widget.
 * @property {string} text - The text displayed on the widget.
 * @property {StyledStates} styledStates - An object that contains style information for different widget states.
 * @property {Object} style - The CSS style of the widget.
 * @property {string} role - The role of the widget.
 * @property {boolean} disabled - Indicates whether the widget is disabled.
 * @property {boolean} isFocusByDefault - Indicates whether the widget is focused by default.
 * @property {boolean} isMainTarget - Indicates whether the widget is the main target of the application.
 * @property {Object} focusDirection - An object that specifies the focus direction of the widget.
 * @property {Object} autoClick - An object that contains information about auto-clicking of the widget.
 * @property {boolean} autoFocus - Indicates whether the widget should be auto-focused.
 * @property {Array} placements - An array of placement objects associated with the widget.
 * @property {boolean} _isModified - Indicates whether the widget has been modified.
 * @property {boolean} [_isFixedWidth]
 */
